package com.appcreator.compose.components.forms

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextField
import androidx.compose.material3.TextFieldDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.focus.FocusDirection
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.semantics.contentType
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import com.appcreator.blueprint.components.forms.InputComponent
import com.appcreator.blueprint.components.basic.MaterialIconComponent
import com.appcreator.blueprint.core.properties.MaterialIcon
import com.appcreator.compose.LocalEnvStore
import com.appcreator.compose.LocalInputEnvStore
import com.appcreator.compose.LocalTheme
import com.appcreator.compose.components.basic.MaterialIconComposable
import com.appcreator.compose.di.Container
import com.appcreator.compose.di.performer
import com.appcreator.compose.extensions.SizingView
import com.appcreator.compose.extensions.composeColor
import com.appcreator.compose.extensions.value
import kotlinx.coroutines.delay

@Composable
fun InputComposable(modifier: Modifier, component: InputComponent) {
    val envStore = LocalEnvStore.current
    val inputEnvStore = LocalInputEnvStore.current
    InputComposable(
        modifier = modifier,
        component = component,
        value = component.data?.value?.let { envStore.get(it) as? String } ?: ""
    ) { new ->
        val maxCharacters = component.maxCharacters?: 0
        val new = if (maxCharacters > 0) {
            new.take(maxCharacters)
        } else new

        if (component.contentType != InputComponent.ContentType.Number
            || new.all { it.isDigit() }
        ) {
            component.data?.value?.let {
                inputEnvStore.set(it, new)
            }
        }
    }
}
@Composable
fun InputComposable(
    modifier: Modifier,
    component: InputComponent,
    value: String,
    onValueChange: (String) -> Unit
) {
    val envStore = LocalEnvStore.current
    val focusManager = LocalFocusManager.current
    val theme = LocalTheme.current

    val onChangeAction = component.onChangeAction?.let { Container.performer(it)?.deferred() }
    LaunchedEffect(value) {
        onChangeAction?.perform(envStore)
    }

    val onChangeDebouncedAction = component.onChangeDebouncedAction?.let { Container.performer(it)?.deferred() }
    LaunchedEffect(value) {
        delay(400) // This acts as a debounce
        onChangeDebouncedAction?.perform(envStore)
    }

    val options = KeyboardOptions(
        imeAction = ImeAction.Done,
        capitalization = when(component.contentType) {
            InputComponent.ContentType.Username,
            InputComponent.ContentType.Password,
            InputComponent.ContentType.NewPassword,
            InputComponent.ContentType.Number,
            InputComponent.ContentType.Email,
            InputComponent.ContentType.Phone -> KeyboardCapitalization.None
            else -> KeyboardCapitalization.Sentences
        },
        keyboardType = when(component.contentType) {
            InputComponent.ContentType.Username -> KeyboardType.Text
            InputComponent.ContentType.Password -> KeyboardType.Password
            InputComponent.ContentType.NewPassword -> KeyboardType.Password
            InputComponent.ContentType.Phone,
            InputComponent.ContentType.Number -> KeyboardType.Number
            InputComponent.ContentType.Email -> KeyboardType.Email
            else -> KeyboardType.Text
        },
        autoCorrectEnabled = when(component.contentType) {
            InputComponent.ContentType.Username,
            InputComponent.ContentType.Password,
            InputComponent.ContentType.NewPassword,
            InputComponent.ContentType.Number -> false
            else -> true
        },
    )
    val actions = KeyboardActions(onDone = {
        if(!focusManager.moveFocus(FocusDirection.Down)) {
            focusManager.clearFocus()
        }
    })

    val password = when(component.contentType) {
        InputComponent.ContentType.Password,
        InputComponent.ContentType.NewPassword -> true
        else -> false
    }

    val singleLine = when(component.contentType) {
        InputComponent.ContentType.MultilineText -> false
        else -> true
    }

    var showPassword by remember { mutableStateOf(false) }
    val visualTransformation = if(password && !showPassword) PasswordVisualTransformation() else VisualTransformation.None


    val trailingIcon: (@Composable () -> Unit)? = if (password && component.passwordVisibilityToggle == true) {
        {
            IconButton(
                onClick = {
                    showPassword = !showPassword
                }
            ) {
                val code = if (showPassword) "\ue8f5" else "\uE8F4"
                MaterialIconComposable(Modifier, MaterialIconComponent(
                    _nodeId = null,
                    _nodeLabel = null,
                    _nodeRelativeId = null,
                    icon = MaterialIcon("", code),
                    size = 18,
                    color = null,
                    fill = false
                ))
            }
        }
    } else if((component.maxCharacters?: 0) > 0) {
        {
            Box(Modifier.padding(horizontal = 4.dp)) {
                Text(
                    text = "${value.length}/${component.maxCharacters}",
                    style = MaterialTheme.typography.labelLarge,
                    color = if (component.maxCharacters == value.length) {
                        MaterialTheme.colorScheme.error
                    } else Color.Unspecified
                )
            }
        }
    } else null

    SizingView(component.width, null) { mod, _, _ ->

        val autofill = when(component.contentType) {
            InputComponent.ContentType.Username -> androidx.compose.ui.autofill.ContentType.Username
            InputComponent.ContentType.Password -> androidx.compose.ui.autofill.ContentType.Password
            InputComponent.ContentType.NewPassword -> androidx.compose.ui.autofill.ContentType.NewPassword
            InputComponent.ContentType.Email -> androidx.compose.ui.autofill.ContentType.EmailAddress
            InputComponent.ContentType.Phone -> androidx.compose.ui.autofill.ContentType.PhoneNumber
            else -> null
        }

        val autofillMod = autofill?.let {
            mod.semantics { contentType = it }
        }?: mod

        when(component.layoutType) {
            InputComponent.LayoutType.Bordered -> OutlinedTextField(
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = theme?.color(component.borderColor)?.composeColor() ?: MaterialTheme.colorScheme.primary
                ),
                modifier = autofillMod.then(modifier),
                value = value,
                onValueChange = onValueChange,
                keyboardOptions = options,
                keyboardActions = actions,
                singleLine = singleLine,
                visualTransformation = visualTransformation,
                label = component.label?.value()?.let {
                    {
                        Text(text = it)
                    }
                },
                placeholder = component.placeholder?.value()?.let {
                    {
                        Text(text = it)
                    }
                },
                trailingIcon = trailingIcon
            )
            InputComponent.LayoutType.Underlined ->
                TextField(
                    modifier = autofillMod.then(modifier),
                    colors = TextFieldDefaults.colors(
                        focusedContainerColor = Color.Transparent,
                        unfocusedContainerColor = Color.Transparent
                    ),
                    value = value,
                    onValueChange = onValueChange,
                    keyboardOptions = options,
                    keyboardActions = actions,
                    singleLine = singleLine,
                    visualTransformation = visualTransformation,
                    label = component.label?.value()?.let {
                        {
                            Text(text = it)
                        }
                    },
                    placeholder = component.placeholder?.value()?.let {
                        {
                            Text(text = it)
                        }
                    }
                )
            else -> BasicTextField(
                modifier = autofillMod.then(modifier).heightIn(min = 44.dp),
                value = value,
                onValueChange = onValueChange,
                keyboardOptions = options,
                keyboardActions = actions,
                singleLine = singleLine,
                visualTransformation = visualTransformation,
                decorationBox = {
                    Box(contentAlignment = Alignment.CenterStart) {
                        it()
                        Row(
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            if (value.isBlank()) {
                                component.placeholder?.value()?.let {
                                    Text(
                                        modifier = Modifier.alpha(0.6f),
                                        text = it
                                    )
                                }
                            }
                            Spacer(Modifier.weight(1f))
                            trailingIcon?.invoke()
                        }
                    }
                }
            )
        }
    }
}
